import React from 'react';
import PropTypes from 'prop-types';
import { Avatar, Box, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { AccessTime as ClockIcon } from '@mui/icons-material';
import { dateFormat, timeFormat } from 'common/constants/date';
import { convertUtcToLocalTime } from 'common/utils/date';
import { getSessionLineColor } from 'common/utils/sessionColor';

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/**
 * Fila COMPACTA para las listas dentro del drawer (400px): línea de color + fecha + hora +
 * avatar del entrenador a la derecha (28px, sin recortarse). Click abre el detalle.
 */
function SessionDrawerListItem({ session, displayStatus = null, dateLocale, onClick }) {
  const theme = useTheme();
  const status = displayStatus ?? session.status;
  const lineColor = getSessionLineColor(theme, status, session.start_date);
  const trainer = session.trainer || session.trainer_id;
  const dayName = capitalize(convertUtcToLocalTime(session.start_date, 'EEEE', dateLocale));
  const dateLabel = convertUtcToLocalTime(session.start_date, dateFormat, dateLocale);
  const startTime = convertUtcToLocalTime(session.start_date, timeFormat);
  const endTime = convertUtcToLocalTime(session.end_date, timeFormat);

  return (
    <Box
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
      sx={{
        cursor: 'pointer',
        backgroundColor: 'surface.raised',
        border: '1px solid',
        borderColor: 'hairline.main',
        borderLeft: `4px solid ${lineColor}`,
        borderRadius: '12px',
        p: 1.25,
        transition: 'background-color 0.2s, transform 0.2s cubic-bezier(0.32,0.72,0,1)',
        '&:hover': { backgroundColor: 'surface.hover', transform: 'translateY(-1px)' }
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} justifyContent="space-between">
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>{dayName}</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{dateLabel}</Typography>
        </Box>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
          <ClockIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
          <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>{startTime} – {endTime}</Typography>
        </Stack>
        {trainer && (
          <Avatar src={trainer.photo} alt={trainer.full_name} sx={{ width: 28, height: 28, fontSize: '0.72rem', flexShrink: 0 }}>
            {trainer.full_name?.[0]}
          </Avatar>
        )}
      </Stack>
    </Box>
  );
}

SessionDrawerListItem.propTypes = {
  session: PropTypes.object.isRequired,
  displayStatus: PropTypes.string,
  dateLocale: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired
};

export default SessionDrawerListItem;
